import { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAllIntegrations, useApiKey } from "../../../hooks/QueryHooks";
import { Badge } from "../../../components/ui/badge";
import { CompanyLogo, getDomainFromCompany } from "../../../components/ui/CompanyLogo";
import notify from "../../../utils/notify";
import IntegrationDetailShell from "../../home/settings/integrations/components/IntegrationDetailShell";
import useSelectWorkspace from "../../../hooks/useSelectWorkspace";
import SegmentSteps from "./components/SegmentSteps";

const SegmentApiPage = () => {
  const selectedWorkspace: any = useSelectWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const setupRootRef = useRef<HTMLDivElement>(null);
  const workspaceSlug = selectedWorkspace?.data?.unique_identifier;
  const workspaceId = selectedWorkspace?.data?.id;
  const isOnboarding = location.pathname.includes("/onboarding/");

  const apiKey: any = useApiKey({
    workspaceId,
    enabled: !!workspaceId,
  });

  const { data: segmentIntegration }: any = useAllIntegrations({
    workspaceId,
    enabled: !!workspaceId,
    select: (response: any) => response?.data?.find((item: any) => item?.source_name === "segment_api"),
    onError: () => {
      notify({
        heading: "Failed to fetch integrations",
        messageList: ["Please try again later or contact support."],
        type: "failure",
      });
    },
  });

  const handleBack = () => {
    if (!isOnboarding && workspaceSlug) {
      navigate(`/home/${workspaceSlug}/settings/integrations`);
      return;
    }
    navigate(-1);
  };

  const scrollToSetup = () => {
    setupRootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const statusLabel = segmentIntegration?.setup_complete ? "Connected" : "Disconnected";
  const statusVariant = segmentIntegration?.setup_complete ? "active" : "error";

  if (isOnboarding) {
    return (
      <div className="w-full space-y-6">
        {/* Back + header */}
        <div>
          <button
            type="button"
            onClick={handleBack}
            className="mb-4 flex items-center gap-1 text-[13px] transition-colors hover:text-foreground"
            style={{ color: "#5C6070" }}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to integrations
          </button>

          <div className="flex items-center gap-3">
            <CompanyLogo
              domain={getDomainFromCompany("Segment")}
              name="Segment"
              className="h-9 w-9"
            />
            <div>
              <h1 className="text-[15px] font-semibold" style={{ color: "#181818" }}>Segment</h1>
              <div className="mt-0.5 flex items-center gap-2">
                <Badge variant="statusPill" status={statusVariant as any}>{statusLabel}</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Status card */}
        <div className="rounded-lg border border-border divide-y divide-border">
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[12px]" style={{ color: "#5C6070" }}>Status</span>
            <span className="text-[12px]" style={{ color: "#181818" }}>{segmentIntegration?.setup_complete ? "Active" : "Inactive"}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[12px]" style={{ color: "#5C6070" }}>Credentials</span>
            <span className="text-[12px]" style={{ color: "#181818" }}>
              {apiKey?.data?.server_analytics_key ? "Valid" : "Invalid"}
            </span>
          </div>
        </div>

        {/* Setup steps */}
        <div ref={setupRootRef} className="rounded-lg border border-border p-4">
          <SegmentSteps
            data={{ apiKey: apiKey?.data, workspace: selectedWorkspace?.data }}
            integration={segmentIntegration}
          />
        </div>
      </div>
    );
  }

  return (
    <IntegrationDetailShell
      integrationName="Segment"
      connected={!!segmentIntegration?.setup_complete}
      credentialOk={!!apiKey?.data?.server_analytics_key}
      externalId={segmentIntegration?.id || workspaceId || null}
      reconnectLabel={segmentIntegration?.setup_complete ? "View setup" : "Continue setup"}
      onReconnect={scrollToSetup}
      onBack={handleBack}
    >
      <div ref={setupRootRef} className="rounded-lg border border-border p-4">
        <SegmentSteps
          data={{ apiKey: apiKey?.data, workspace: selectedWorkspace?.data }}
          integration={segmentIntegration}
        />
      </div>
    </IntegrationDetailShell>
  );
};

export default SegmentApiPage;
